/**
 * AdminUsers - Team management page
 * Create, edit, activate/deactivate admin users (owner/director only for most ops)
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch, hasPermission, isRole } from './api';

const ROLE_COLORS = {
  owner: '#C084FC', director: '#3B82F6', manager: '#10B981', agent: '#F59E0B', viewer: '#6B7280',
};

function formatDate(d) {
  if (!d) return 'Never';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'agent', phone: '', title: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const fetchUsers = () => {
    adminFetch('/api/admin/auth/users')
      .then(r => r.json())
      .then(d => { setUsers(d.users || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const res = await adminFetch('/api/admin/auth/users', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create user');
        setCreating(false);
        return;
      }
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'agent', phone: '', title: '' });
      fetchUsers();
    } catch (err) {
      setError('Network error');
    }
    setCreating(false);
  };

  const toggleActive = async (userId, currentActive) => {
    await adminFetch(`/api/admin/auth/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: !currentActive }),
    });
    fetchUsers();
  };

  const s = {
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
    createBtn: {
      padding: '10px 20px', borderRadius: 8, border: 'none',
      background: 'linear-gradient(135deg, #0057FF, #0040CC)', color: '#FFF',
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    card: {
      backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700,
      color: '#5A6175', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2A3444',
    },
    td: { padding: '14px 16px', fontSize: 14, color: '#BBBEC7', borderBottom: '1px solid #1A2332' },
    modal: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 200,
    },
    modalCard: { backgroundColor: '#0F1729', borderRadius: 16, border: '1px solid #2A3444', padding: 32, width: 440 },
    input: {
      width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #2A3444',
      backgroundColor: '#1A2332', color: '#FFF', fontSize: 14, outline: 'none', boxSizing: 'border-box',
    },
    field: { marginBottom: 16 },
    label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#8B92A5', marginBottom: 4 },
    error: {
      backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '10px 14px', color: '#EF4444', fontSize: 13, marginBottom: 16,
    },
  };

  return (
    <AdminShell title="Team Management">
      <div style={s.topBar}>
        <div style={{ fontSize: 14, color: '#5A6175' }}>{users.length} team member{users.length !== 1 ? 's' : ''}</div>
        {hasPermission('users', 'create') && (
          <button style={s.createBtn} onClick={() => setShowCreate(true)}>+ Add Team Member</button>
        )}
      </div>

      <div style={s.card}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>Loading...</div>
        ) : users.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>No team members yet. Use setup to create the owner account first.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Role</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Last Login</th>
                <th style={s.th}>Created</th>
                {hasPermission('users', 'edit') && <th style={s.th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const roleColor = ROLE_COLORS[u.role] || '#6B7280';
                return (
                  <tr key={u.id}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: '#FFF' }}>{u.name}</div>
                      <div style={{ fontSize: 12, color: '#5A6175' }}>{u.email}</div>
                      {u.title && <div style={{ fontSize: 11, color: '#5A6175' }}>{u.title}</div>}
                    </td>
                    <td style={s.td}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', backgroundColor: `${roleColor}20`, color: roleColor,
                      }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        backgroundColor: u.is_active ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                        color: u.is_active ? '#10B981' : '#EF4444',
                      }}>
                        {u.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td style={s.td}>{formatDate(u.last_login_at)}</td>
                    <td style={s.td}>{formatDate(u.created_at)}</td>
                    {hasPermission('users', 'edit') && (
                      <td style={s.td}>
                        {u.role !== 'owner' && (
                          <button
                            style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #2A3444', backgroundColor: 'transparent', color: u.is_active ? '#F59E0B' : '#10B981', fontSize: 12, cursor: 'pointer' }}
                            onClick={() => toggleActive(u.id, u.is_active)}
                          >
                            {u.is_active ? 'Disable' : 'Enable'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div style={s.modal} onClick={() => setShowCreate(false)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600 }}>Add Team Member</h3>
            {error && <div style={s.error}>{error}</div>}
            <form onSubmit={handleCreate}>
              <div style={s.field}>
                <label style={s.label}>Full Name</label>
                <input style={s.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Email</label>
                <input style={s.input} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Temporary Password</label>
                <input style={s.input} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={10} placeholder="Min 10 characters" />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Role</label>
                  <select style={s.input} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                    <option value="viewer">Viewer</option>
                    <option value="agent">Agent</option>
                    <option value="manager">Manager</option>
                    {isRole('owner') && <option value="director">Director</option>}
                    {isRole('owner') && <option value="owner">Owner</option>}
                  </select>
                </div>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Job Title</label>
                  <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Account Manager" />
                </div>
              </div>
              <div style={s.field}>
                <label style={s.label}>Phone</label>
                <input style={s.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="button" style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #2A3444', backgroundColor: 'transparent', color: '#8B92A5', fontSize: 13, fontWeight: 600, cursor: 'pointer', flex: 1 }} onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" style={{ ...s.createBtn, flex: 1 }} disabled={creating}>{creating ? 'Creating...' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
